import { vec3 } from 'gl-matrix';

import { BoundingBox, BoundingBoxObject } from './BoundingBox';
import { BoundingSphere } from './BoundingSphere';

import { TRS } from './TRS';
import { Ray } from '../math';

export class BoundingDimensions implements BoundingBoxObject {
  public boundingBox!: BoundingBox;
  public boundingSphere!: BoundingSphere;

  public center!: vec3;
  public maxDimensions!: vec3;
  public localMin!: vec3;
  public localMax!: vec3;
  public position: vec3;

  private object!: TRS;

  constructor() {
    this.position = vec3.create();
    this.localMin = vec3.create();
    this.localMax = vec3.create();
  }

  getBoundingBox(): BoundingBox {
    return this.boundingBox;
  }

  canIntersectRay(): boolean {
    return false;
  }

  intersectsRay(ray: Ray): boolean {
    return false;
  }

  private init(): void {
    let maxDimension = Math.max(...this.maxDimensions);

    let min = vec3.create();
    let max = vec3.create();
    let half = vec3.fromValues(maxDimension, maxDimension, maxDimension);

    vec3.add(max, this.center, half);
    vec3.sub(min, this.center, half);

    this.boundingBox = new BoundingBox(min, max);
    this.boundingSphere = new BoundingSphere(this.center, maxDimension);
  }

  public static createFromData(
    data: Float32Array | number[],
    vertexCount: number,
    positionOffset: number,
    stride: number,
  ): BoundingDimensions {
    let sum = vec3.create();
    let min = vec3.fromValues(Infinity, Infinity, Infinity);
    let max = vec3.fromValues(-Infinity, -Infinity, -Infinity);

    for (let i = 0; i < vertexCount; i++) {
      let offset = i * stride + positionOffset;

      for (let v = 0; v < 3; v++) {
        let s = data[offset + v];

        sum[v] += s;
        if (s < min[v]) min[v] = s;
        if (s > max[v]) max[v] = s;
      }
    }

    let result = new BoundingDimensions();
    result.center = vec3.divide(sum, sum, [vertexCount, vertexCount, vertexCount]);
    result.maxDimensions = vec3.fromValues(
      Math.abs(min[0] - max[0]),
      Math.abs(min[1] - max[1]),
      Math.abs(min[2] - max[2]),
    );
    result.localMin = vec3.clone(min);
    result.localMax = vec3.clone(max);
    result.init();
    return result;
  }

  // public static createFromFaces(faces: Face[]): BoundingDimensions {
  //     let sum = vec3.create();
  //     let min = vec3.fromValues(Infinity, Infinity, Infinity);
  //     let max = vec3.fromValues(-Infinity, -Infinity, -Infinity);
  //     let vertexCount = faces.length * 3;
  //     for (let face of faces) {
  //         for (let vertex of face.vertices) {
  //             for (let v = 0; v < 3; v++) {
  //                 let s = vertex.position[v];
  //
  //                 sum[v] += s;
  //                 if (s < min[v]) min[v] = s;
  //                 if (s > max[v]) max[v] = s;
  //             }
  //         }
  //     }
  //
  //     let result = new BoundingDimensions();
  //     result.center = vec3.divide(sum, sum, [vertexCount, vertexCount, vertexCount]);
  //     result.maxDimensions = vec3.fromValues(
  //         Math.abs(min[0] - max[0]),
  //         Math.abs(min[1] - max[1]),
  //         Math.abs(min[2] - max[2])
  //     );
  //     result.init();
  //     return result;
  // }

  public static createFromPrimitives(
    primitives: {
      attributes: {
        [key: string]: { array: { [i: number]: number; length: number } };
      };
    }[],
  ): BoundingDimensions {
    let sum = vec3.create();
    let min = vec3.fromValues(Infinity, Infinity, Infinity);
    let max = vec3.fromValues(-Infinity, -Infinity, -Infinity);
    let vertexCount = 0;

    for (const prim of primitives) {
      const pos = prim.attributes['POSITION'];
      if (!pos) continue;
      const arr = pos.array;
      const count = arr.length / 3;
      vertexCount += count;

      for (let i = 0; i < arr.length; i += 3) {
        for (let v = 0; v < 3; v++) {
          const s = arr[i + v];
          sum[v] += s;
          if (s < min[v]) min[v] = s;
          if (s > max[v]) max[v] = s;
        }
      }
    }

    if (vertexCount === 0) return new BoundingDimensions();

    let result = new BoundingDimensions();
    result.center = vec3.divide(sum, sum, [vertexCount, vertexCount, vertexCount]);
    result.maxDimensions = vec3.fromValues(
      Math.abs(min[0] - max[0]),
      Math.abs(min[1] - max[1]),
      Math.abs(min[2] - max[2]),
    );
    result.localMin = vec3.clone(min);
    result.localMax = vec3.clone(max);
    result.init();
    return result;
  }

  public static createFromBox(min: vec3 | number[], max: vec3 | number[]): BoundingDimensions {
    let result = new BoundingDimensions();
    let diff = [...vec3.sub(vec3.create(), max, min)];
    let radius = Math.max(...diff.map(Math.abs));

    let half = vec3.divide(diff, diff, [2, 2, 2]);
    let center = vec3.add(vec3.create(), min, half);

    result.boundingBox = new BoundingBox(min, max);
    result.boundingSphere = new BoundingSphere(center, radius);

    result.center = center;
    result.maxDimensions = vec3.fromValues(radius, radius, radius);

    return result;
  }

  public static createFromSphere(center: vec3 | number[], radius: number): BoundingDimensions {
    throw new Error('Not implemented');
  }

  public bind(object: TRS): void {
    this.object = object;
    this.object.changed.subscribe(() => this.update());
    this.update();
  }

  public update(): void {
    let worldCenter = vec3.transformMat4(vec3.create(), this.center, this.object.matrix);
    let maxDimension = Math.max(...this.maxDimensions) * this.object.scale;

    let half = vec3.fromValues(maxDimension, maxDimension, maxDimension);
    let min = vec3.sub(vec3.create(), worldCenter, half);
    let max = vec3.add(vec3.create(), worldCenter, half);

    this.boundingBox.min = min;
    this.boundingBox.max = max;

    this.boundingSphere.position = worldCenter;
    this.boundingSphere.radius = maxDimension;
  }

  public clone(): BoundingDimensions {
    let result = new BoundingDimensions();
    result.position = vec3.clone(this.position);
    result.maxDimensions = vec3.clone(this.maxDimensions);
    result.center = vec3.clone(this.center);
    result.localMin = vec3.clone(this.localMin);
    result.localMax = vec3.clone(this.localMax);

    result.boundingSphere = this.boundingSphere.clone();
    result.boundingBox = this.boundingBox.clone();

    return result;
  }
}
